/**
 * @fileoverview Componente molecule ContextAwareToggleMC para toggles de acordeón con contexto de equipo y optimización de renderizado.
 * Permite expandir/colapsar secciones de equipos mostrando color e icono dinámico.
 * Optimizado con React.memo, useMemo y useCallback.
 *
 * @module ContextAwareToggleMC
 * @category Component - Molecule
 * @since 1.0.0
 * @version 1.1.0 - Documentación JSDoc completa y optimización de renderizado.
 */

import React, { useContext, memo, useCallback, useMemo } from 'react';
import AccordionContext, { isAccordionItemSelected } from 'react-bootstrap/AccordionContext';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';
import { useTraceUpdate } from '../../hooks/shared/useDebugRenders';

import DrownArrowIcon from 'images/arrow-down-24.svg';
import ArrowUpIcon from 'images/arrow-up-24.svg';

/**
 * @typedef {Object} ContextAwareToggleMCProps
 * @description Propiedades del componente ContextAwareToggleMC.
 * @property {Object} team - Objeto de equipo con color y nombre
 * @property {string|number} eventKey - Clave del acordeón
 * @property {function} [callback] - Callback al hacer toggle
 */

/**
 * Componente molecule para toggles de acordeón con contexto de equipo y optimización de renderizado.
 *
 * @component ContextAwareToggleMC
 * @category Component - Molecule
 *
 * @description
 * **Características:**
 * - Toggle de acordeón con icono dinámico (expandir/colapsar)
 * - Muestra color y nombre del equipo
 * - Optimizado con React.memo, useMemo y useCallback para evitar renders innecesarios
 * - Integración con react-bootstrap AccordionContext
 * - Estilizado con TailwindCSS
 *
 * **Atomic Design:**
 * - Nivel: Molecule
 * - Composición: Usa iconos SVG, contexto de acordeón y props de equipo
 * - Dependencias: AccordionContext, useAccordionButton, useTraceUpdate
 *
 * @param {ContextAwareToggleMCProps} props - Propiedades del componente
 * @returns {JSX.Element} Elemento renderizado del toggle
 *
 * @example
 * <ContextAwareToggleMC team={team} eventKey={1} callback={fn} />
 *
 * @see {@link ../../hooks/shared/useDebugRenders} Hook para debug de renders
 *
 * @since 1.0.0
 * @version 1.1.0 - Documentación JSDoc completa y optimización
 */
const ContextAwareToggleMC = memo(({
  team,
  eventKey,
  callback
}) => {
  const { activeEventKey } = useContext(AccordionContext);

  useTraceUpdate('ContextAwareToggleMC', { team, eventKey, callback, activeEventKey }, {
    enabled: process.env.NODE_ENV === 'development',
    logRenderCount: true,
    logPropChanges: true,
    logStackTrace: false
  });


  /**
   * Determina si el acordeón está expandido para el eventKey actual.
   * @function isCurrentEventKey
   * @memberof ContextAwareToggleMC
   * @returns {boolean}
   */
  const isCurrentEventKey = useMemo(
    () => isAccordionItemSelected(activeEventKey, eventKey),
    [activeEventKey, eventKey]
  );


  /**
   * Selecciona el icono según el estado del acordeón.
   * @function iconSrc
   * @memberof ContextAwareToggleMC
   * @returns {string} Ruta del icono
   */
  const iconSrc = useMemo(() => {
    return isCurrentEventKey ? ArrowUpIcon : DrownArrowIcon;
  }, [isCurrentEventKey]);


  /**
   * Callback decorado para manejar el toggle del acordeón.
   * @function decoratedOnClick
   * @memberof ContextAwareToggleMC
   * @returns {function} Callback para el botón
   */
  const decoratedOnClick = useAccordionButton(
    eventKey,
    useCallback(() => {
      callback && callback(eventKey);
    }, [callback, eventKey]),
  );


  /**
   * Memoiza el estilo de color del equipo.
   * @function teamColorStyle
   * @memberof ContextAwareToggleMC
   * @returns {Object} Estilo CSS para el color
   */
  const teamColorStyle = useMemo(() => ({
    backgroundColor: team.color
  }), [team.color]);

  return (
    <div className='tw-wrapper tw-flex tw-w-full'>
      <button
        type="button"
        className='tw-accordion-button tw-py-5 tw-border-0 tw-bg-transparent tw-w-full'
        onClick={decoratedOnClick}
        aria-expanded={isCurrentEventKey}
        aria-controls={`collapse-${eventKey}`}
      >
        <div className="tw-flex tw-flex-row tw-items-center">
          <img 
            src={iconSrc} 
            className='tw-mr-2' 
            alt={isCurrentEventKey ? 'Collapse' : 'Expand'}
            width={24}
            height={24}
          />
          <div className='tw-flex tw-items-center tw-mr-2'>
            <span 
              className='tw-w-3 tw-h-3 tw-rounded-full tw-inline-block' 
              style={teamColorStyle}
              aria-label={`Team color: ${team.color}`}
            />
          </div>
          <span className='tw-text-sm tw-text-gray-700 tw-font-semibold'>
            {team.name}
          </span>
        </div>
      </button>
    </div>
  );
});


/**
 * Nombre de display para el componente ContextAwareToggleMC.
 * @type {string}
 */
ContextAwareToggleMC.displayName = 'ContextAwareToggleMC';

export default ContextAwareToggleMC;
